import React, { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) return setError("New passwords do not match");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      localStorage.setItem("gt_token", data.token);
      toast.success("Password changed");
      setOpen(false);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const fields = [["current", "Current Password"], ["next", "New Password"], ["confirm", "Confirm New Password"]];
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); setError(""); }}>
      <DialogTrigger asChild>
        <button data-testid="change-password-button"
          className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-sm transition-colors">
          <KeyRound className="w-4 h-4" /> Change password
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm bg-card max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Enter your current password, then choose a new one (8+ characters).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {fields.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em]">{label}</Label>
              <Input type="password" value={form[k]} onChange={set(k)} minLength={k === "current" ? 1 : 8} required
                data-testid={`change-password-${k}-input`} className="rounded-sm h-10" />
            </div>
          ))}
          {error && <div data-testid="change-password-error" className="text-sm text-destructive">{error}</div>}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="change-password-submit" className="rounded-sm bg-black hover:bg-neutral-800 text-white">
              {saving ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
